'use client';

import React from 'react';
import Link from 'next/link';

// Define the mapping from dimension name to case study slug
const dimensionToSlugMap: { [key: string]: string } = {
  'Sales Segmentation': 'dj-sales',
  Influencers: 'dj-influence',
  'Channel Preference': 'dj-channel',
  'Communication Style': 'dj-style',
  Persona: 'dj-persona',
  Adoption: 'dj-adopter',
  'Implementation/ Insights': 'dj-activate',
};

interface ResultListItemProps {
  dimensionName: string;
  score: number;
  industryAvg: number;
  type: 'strength' | 'improvement';
}

const ResultListItem: React.FC<ResultListItemProps> = ({
  dimensionName,
  score,
  industryAvg,
  type,
}) => {
  const isStrength = type === 'strength';
  const comparisonText = isStrength
    ? `Your score of ${score}% is above the industry average of ${industryAvg}%.`
    : `Your score of ${score}% is ${score === industryAvg ? 'at' : 'below'} the industry average of ${industryAvg}%.`;

  const indicatorIcon = isStrength ? '↑' : '↓';
  const indicatorColor = isStrength ? 'text-primary-green' : 'text-primary-red';

  // Get the slug based on the dimension name
  const caseStudySlug = dimensionToSlugMap[dimensionName];
  const caseStudyUrl = caseStudySlug ? `/case-studies/${caseStudySlug}` : null;

  return (
    <li className="mb-4 flex flex-col items-stretch gap-3 sm:mb-3 sm:flex-row sm:items-center sm:gap-2">
      {/* Text Section - Add inner flex container */}
      <div className="flex-1 text-center sm:text-left flex items-start gap-2">
        {/* Arrow Indicator */}
        <span className={`text-xl ${indicatorColor} leading-tight`}>{indicatorIcon}</span>
        {/* Text Content Block - Wrap in div with flex-1 and add text color */}
        <div className="flex-1 text-gray-200">
          <strong className="font-semibold">{dimensionName}:</strong> {comparisonText}
          {/* Replace span with Link if URL exists */}
          {caseStudyUrl && (
            <Link
              href={caseStudyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 cursor-pointer text-sm text-[#59B9C1] underline hover:text-[#4aa7ae]"
            >
              (View Case Study)
            </Link>
          )}
        </div>
      </div>
    </li>
  );
};

export default ResultListItem;
